import {
  faFileDownload,
  faSpinner,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { UseQueryResult } from "@tanstack/react-query";
import { FC, useCallback, useState } from "react";
import { Alert, Anchor, Button, Col, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../../../shared/src/components/ConfirmationModal";
import useLoading from "../../../../../shared/src/hooks/useLoading";
import useAllowedMimeTypeToExtensionsMap from "../../../../modules/filestore/hooks/useAllowedMimeTypeToExtensionsMap";
import useDownloadCaseAttachment from "../../../../modules/filestore/hooks/useDownloadCaseAttachment";
import { EUFinanceFileStoreDeleteFileMetadata } from "../../../../modules/filestore/models/metadata/EUFinanceFilestoreDeleteFileMetadata";
import { EUFinanceCaseAttachment } from "../../../../modules/filestore/models/sobjects/EUFinanceCaseAttachment";
import { useEUFinanceFileStoreService } from "../../../../modules/filestore/services/EUFinanceFileStoreService";
import {
  getFileIcon,
  openFileInNewTab,
} from "../../../../modules/filestore/utils/FileUtil";

const DOCUMENTS_CLASSNAME = "eu-finance-case-attachment-uploader__documents";

export type DocumentsProps = {
  files: EUFinanceCaseAttachment[];
  refreshFiles: UseQueryResult<EUFinanceCaseAttachment[]>["refetch"];
  disableDelete?: boolean;
  disableDownload?: boolean;
};

const CaseAttachments: FC<DocumentsProps> = ({
  files,
  refreshFiles,
  disableDelete = false,
  disableDownload = false,
}) => {
  const fileStoreService = useEUFinanceFileStoreService();
  const loading = useLoading();
  const [fileToDelete, setFileToDelete] = useState<
    EUFinanceCaseAttachment | undefined
  >();
  const [isDownloadingFileByFileIdMap, setIsDownloadingFileByFileIdMap] =
    useState<Record<string, boolean>>({});
  const { data: allowedMimeTypeToExtensionsMap } =
    useAllowedMimeTypeToExtensionsMap();

  const { downloadFile } = useDownloadCaseAttachment({
    openFile: openFileInNewTab,
    setIsDownloadingFileByFileIdMap,
    onError: toast.error,
    onDownloadStart: toast.info,
    onDownloadComplete: toast.success,
  });

  const deleteFile = useCallback(
    async (file: EUFinanceCaseAttachment) => {
      if (!file.fileName || !file.id) {
        toast.error("File name or ID is missing");
        return;
      }
      await loading(async () => {
        try {
          const metadata = {
            caseId: file.caseId,
            filePath: encodeURIComponent(file.filePath ?? ""),
            fileName: file.fileName as string,
            caseAttachmentId: file.id as string,
          } as EUFinanceFileStoreDeleteFileMetadata;

          const response = await fileStoreService.deleteFileByKeyAndMetadata(
            file.id as string,
            metadata
          );

          if (response.status === 200) {
            toast.success(`Successfully deleted file: ${file.fileName}`);
            await refreshFiles();
          } else {
            toast.error(`Failed to delete file: ${file.fileName}`);
          }
        } catch (error) {
          toast.error("An error occurred while deleting the file", {
            autoClose: false,
          });
        }
      });
    },
    [fileStoreService, loading, refreshFiles]
  );

  const handleDeleteFile = useCallback(() => {
    setFileToDelete((previousFileToDelete) => {
      if (previousFileToDelete) {
        deleteFile(previousFileToDelete);
        return undefined;
      }
      return previousFileToDelete;
    });
  }, [deleteFile]);

  return (
    <div className={DOCUMENTS_CLASSNAME}>
      <Row>
        <Col>
          {files.length === 0 ? (
            <Alert variant="info" className="mt-3">
              No attachments found
            </Alert>
          ) : (
            files.map((file) => {
              const isDownloadingFile =
                isDownloadingFileByFileIdMap[file?.id as string];
              return (
                <div
                  key={file.id}
                  className="d-flex justify-content-between p-2 border mt-2"
                >
                  <div className="align-content-center">
                    <FontAwesomeIcon
                      icon={getFileIcon(allowedMimeTypeToExtensionsMap, file)}
                      className="me-3"
                      onClick={() => setFileToDelete(file)}
                    />
                    <Anchor
                      onClick={() => downloadFile?.(file)}
                      disabled={isDownloadingFile || disableDownload}
                    >
                      {file.fileName}
                    </Anchor>
                  </div>
                  <div>
                    {!disableDownload && (
                      <Button
                        variant="success"
                        className="text-white me-2"
                        onClick={() => downloadFile?.(file)}
                        disabled={isDownloadingFile}
                      >
                        {isDownloadingFile ? (
                          <>
                            <FontAwesomeIcon
                              icon={faSpinner}
                              spin
                              size="1x"
                              className="me-2"
                            />
                            Downloading
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon
                              icon={faFileDownload}
                              className="me-2"
                            />
                            Download
                          </>
                        )}
                      </Button>
                    )}
                    {!disableDelete && (
                      <Button
                        variant="danger"
                        className="text-white"
                        onClick={() => setFileToDelete(file)}
                        disabled={isDownloadingFile}
                      >
                        <FontAwesomeIcon icon={faTrash} className="me-2" />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </Col>
      </Row>
      <ConfirmationModal
        id={`${DOCUMENTS_CLASSNAME}__delete-file-modal`}
        confirmButtonProps={{
          variant: "danger",
        }}
        show={fileToDelete !== undefined}
        title={`Delete Attachment: ${fileToDelete?.fileName}`}
        body={`Are you sure you want to delete this attachment "${fileToDelete?.fileName}"?`}
        onCancel={() => setFileToDelete(undefined)}
        onConfirm={handleDeleteFile}
      />
    </div>
  );
};

CaseAttachments.defaultProps = {
  disableDelete: false,
  disableDownload: false,
};

export default CaseAttachments;
